import {
  changeAssessmentType,
  clinicActionAllowPatientAssessments,
  clinicActionDisablePatientAssessments,
  clinicActionEditPatientDetails
} from 'lib/api/firebase/apis/apis';
import { stringOrNull } from 'lib/utils/string/normalise';
import { useMemo } from 'react';
import { useCopyToClipboardMutation } from '../../../../../../../lib/hooks/tanstack/use-CopyToClipboardMutation';
import {
  ActionState,
  ActionTagSettings
} from '../../../../../../../components/action-tag/action-tag-component';
import { ActionTagEdit } from '../components/action-edit';
import {
  ACTION_SENDSMSINVITE_ONCLICK,
  ActionSendSMSInvite
} from '../components/action-send-sms-invite';
import { ACTION_COPYINVITELINK } from '../../configs/config-action-copyinvitelink';
import { BASE_ACTIONS } from '../../configs/shared-actions-baseconfig';
import { PatientRecord } from '../types';
import { useGetClinicAllInfo } from 'lib/hooks/tanstack/use-getClinicInfo';
import { TableRow } from '../../user-table-types';

/**
 * Hook that resolves the action menu for a patient record.
 *
 * @param clinicId - The identifier of the clinic in context.
 * @returns An array of the available actions for each patient row.
 */
export function useResolvePatientTableActions(clinicId: string) {
  const copyToClipboardMutation = useCopyToClipboardMutation();
  const clinicInfo = useGetClinicAllInfo(clinicId);
  const FEATURE_FLAG_ASSESSMENT_BASELINE =
    !!clinicInfo.clinicInfo?.clinic.crx_settings?.FEATURE_FLAGS
      .ASSESSMENT_BASELINE;
  return useMemo<ActionTagSettings<TableRow<PatientRecord>>[]>(
    () => [
      /**
       * Include base table actions
       */
      ...(BASE_ACTIONS as unknown as ActionTagSettings<
        TableRow<PatientRecord>
      >[]),
      /**
       * Send or resent a SMS invite for a pending invitation
       */
      {
        name: 'Send SMS invite',
        shouldShow: p => p.invitePending,
        initialState: () => ActionState.NEUTRAL,
        render: _props => (
          <ActionSendSMSInvite clinicId={clinicId} {..._props} />
        ),
        onClick: ACTION_SENDSMSINVITE_ONCLICK(clinicId),
        onSuccessSettings: {
          blockOnDone: false,
          timeout: 1000
        },
        onSuccess: refetch => refetch()
      },
      /**
       * LOCK/UNLOCK assessments
       */
      {
        name: p =>
          !p.raw.canStartAssessments
            ? 'Activate Assessment'
            : 'Disable Assessment',
        onClick: async p => {
          if (p.raw.id === undefined) {
            throw new Error('ID is undefined. Please try reloading the page');
          }
          if (p.raw.canStartAssessments) {
            return clinicActionDisablePatientAssessments(p.raw.id);
          } else {
            return clinicActionAllowPatientAssessments(p.raw.id);
          }
        },
        shouldShow: p => !p.invitePending,
        toolTip: p =>
          p.raw.canStartAssessments
            ? `Click to disallow this test-taker from starting new assessments`
            : `Click to allow this test-taker to start new assessments`,
        onSuccess: refetch => refetch()
      },
      {
        name: 'Generate Assessment Link',
        onClick: () => !!0,
        // TODO: Enable this functionality
        shouldShow: false,
        onSuccess: refetch => refetch(),
        toolTip:
          'Click to generate a link that allows the test-taker to log in and complete their ConcussionRx Assessment with no login'
      },
      /**
       * Change the assessment type
       */
      {
        name: p =>
          `Change next assessment to: ${
            p.raw.nextAssessmentType === 'baseline'
              ? 'post-injury'.toUpperCase()
              : 'baseline'.toUpperCase()
          }`,
        onClick: async p => {
          if (p.raw.id === undefined) {
            throw new Error('ID is undefined. Please try reloading the page');
          }

          return changeAssessmentType(
            p.raw.id,
            p.raw.nextAssessmentType === 'baseline' ? 'post-injury' : 'baseline'
          );
        },
        shouldShow: p =>
          FEATURE_FLAG_ASSESSMENT_BASELINE === true && !p.invitePending,
        onSuccess: refetch => refetch()
      },

      /**
       * Edit Test-taker details
       */
      {
        name: 'Edit',
        initialState: () => ActionState.NEUTRAL,
        render: _props => <ActionTagEdit clinicId={clinicId} {..._props} />,
        /**
         *
         * TODO: Add type definition to `extraData`
         * @param p
         * @param extraData
         * @returns
         */
        onClick: (p, extraData) => {
          if (!p.raw.id) {
            throw new Error('ID is undefined. Please try reloading the page');
          }

          return clinicActionEditPatientDetails({
            patientId: p.raw.id,
            firstName: extraData.firstName,
            lastName: extraData.lastName,
            email: extraData.email,
            /**
             * Convert empty strings to null
             */
            phoneNumber: stringOrNull(extraData.phoneNumber),
            clinicianId: extraData.clinicianId,
            assessmentType: extraData.assessmentType,
            clinicId: clinicId
          });
        },
        onSuccessSettings: {
          blockOnDone: false,
          timeout: 1000
        },
        onSuccess: refetch => refetch()
      },

      /**
       * Include the copy invite link action
       */
      ACTION_COPYINVITELINK(copyToClipboardMutation.mutateAsync)
    ],
    [
      clinicId,
      copyToClipboardMutation.mutateAsync,
      FEATURE_FLAG_ASSESSMENT_BASELINE
    ]
  );
}
